import moment from "moment";
import { adamActionsConfig } from "./adam";
import { noticeCodes } from "./genesys";
import { ConversationType } from "./types";
import { isIncomingMessage, isMamMessage, getParseTextContentFromHtml, } from "./functions";
import strings from "../static/strings";
import WebChatCore from "@monksoftware/webchat-core-wfp/dist/MonkWebChatCoreWfp.esm";
// we can overide methods and do other customizations
// to the webchat-core module
// thanks to the .extend() from vue2
export default WebChatCore.extend({
    methods: {
        /**
         * @returns {VueInstance} The instance of App component.
         */
        getInstance() {
            return this.$parent;
        },
        /**
         * @returns {VueInstance} Returns the instance of Webchat component reference.
         */
        getWebchatInstance() {
            return this.getInstance().$refs.webchat;
        },
        /**
         * If "key" is in localStorage this method returns its decrypted value.
         * @param {string} key - The localStorage key
         * @returns {(string|null)} Decrypted value of key otherwise null
         */
        _slsGet(key) {
            return this.getInstance()._slsGet(key);
        },
        /**
         * Set (encrypted) value into localStorage
         * @param {string} key - The localStorage key
         * @param {any} value - The corresponding value
         * @returns {void}
         */
        _slsSet(key, value) {
            return this.getInstance()._slsSet(key, value);
        },
        /**
         * Returns the current conversation thread or boolean
         * @returns {(string|false)}
         */
        getCurrentConversationThread() {
            if (this.getInstance().canShowConversationsView) {
                return false;
            }
            const { class: currentChatClass, thread } = this.getWebchatInstance().currentChat;
            if (currentChatClass && thread) {
                const isWfpChat = currentChatClass === ConversationType.Wfp;
                return !isWfpChat ? thread : this.getInstance().getMainThread();
            }
            else {
                return this.getInstance().getMainThread();
            }
        },
        getMessageSender(msg = {}) {
            let sender = "user";
            const { payload } = msg;
            if (!payload) {
                return sender;
            }
            const { data } = payload;
            if (!data) {
                return sender;
            }
            const { conversationType } = data;
            return conversationType || sender;
        },
        getConstructedStoreMessage(msg = {}) {
            // Check for current conversation thread
            const keyThread = this.isMessageHasStanza(msg) && msg.stanza.thread !== "anonymous" ?
                msg.stanza.thread : this.getCurrentConversationThread();
            // Check for message sender
            const sender = this.getMessageSender(msg);
            msg = Object.assign({}, this.getHandleConversationTypeSwitch(msg, sender));
            // Use a key to reference the message into history
            return Object.assign({}, msg, { key: keyThread });
        },
        /**
         * Update chat UI (title & header icon) based on incoming message
         * @param {any} message - Incoming message
         * @param {string} sender - Message sender (bot | operator)
         * @returns {void}
         */
        getHandleConversationTypeSwitch(message, sender) {
            let chatHeaderIcon = "";
            if (isIncomingMessage(message) &&
                message.stanza &&
                message.stanza.thread === this.getCurrentConversationThread()) {
                if (sender === "operator") {
                    const chatHeaderClass = "wfp";
                    document.getElementById("chat-header")?.classList.add(chatHeaderClass);
                    chatHeaderIcon = this.getInstance()._getRenderedAsset.avatar_operatore;
                }
                else if (sender === "bot") {
                    document.getElementById("chat-header")?.classList.add("wfp");
                    chatHeaderIcon = this.getInstance()._getRenderedAsset.ico_wfp;
                }
            }
            if (chatHeaderIcon) {
                this.getWebchatInstance().setChatHeaderIcon(chatHeaderIcon);
            }
            return this.getSingleMessagesAvatar(message, sender);
        },
        /**
         * Update message avatar based on incoming message
         * @param {any} message - Incoming message
         * @param {string} sender - Message sender (bot | operator)
         * @returns {void}
         */
        getSingleMessagesAvatar(message, sender) {
            const { type, avatar, stanza } = message;
            if (type === "chat_notice" || avatar) {
                return message;
            }
            const direction = stanza ? stanza.direction : "out";
            // default avatar
            let fromAvatar = this.getInstance()._getRenderedAsset.ico_user;
            if (direction === "in") {
                // wfp avatar
                if (sender === "operatore") {
                    fromAvatar = this.getInstance()._getRenderedAsset.ico_operatore; // operator
                }
                else {
                    fromAvatar = this.getInstance()._getRenderedAsset.avatar_wfp;
                }
            }
            return Object.assign({}, message, { fromAvatar });
        },
        /**
         * Returns corrected message object needed for internal functions
         * @param {object} msg - the message object payload
         * @returns {object} message object with specific properties
         * i.e. { type, action, text }
         */
        getMessageChatTextPayload(msg = {}) {
            const { type, action, payload } = msg;
            if (type && action && payload) {
                const { text } = payload;
                return { type, action, text };
            }
            return { type, action, text: "" };
        },
        /**
         * Validate input message
         * @param {string} input - the string message from the user input field who interacts with the bot
         * @returns {boolean}
         */
        isMessageInputValidated(input) {
            const pattern = /^[^<>]+$/;
            return pattern.test(input);
        },
        isMessageInputLocation(message = {}) {
            const { payload } = message;
            if (!payload) {
                return false;
            }
            const { inputData } = payload;
            if (!inputData) {
                return false;
            }
            const { interaction } = inputData;
            return interaction && interaction.type === "insert_location";
        },
        isMessageInputDate(message = {}) {
            const { payload } = message;
            if (!payload) {
                return false;
            }
            const { inputData } = payload;
            if (!inputData) {
                return false;
            }
            const { interaction } = inputData;
            return interaction && interaction.type === "input_date";
        },
        isMessageSingleAutocomplete(message = {}) {
            const { payload } = message;
            if (!payload) {
                return false;
            }
            const { inputData } = payload;
            if (!inputData) {
                return false;
            }
            return this.getWebchatInstance()
                .isChoiceSelectionSingleAutoComplete(message, this.getInstance().singleChoiceLimitCount);
        },
        isMessageMultipleAutocomplete(message = {}) {
            const { payload } = message;
            if (!payload) {
                return false;
            }
            const { inputData } = payload;
            if (!inputData) {
                return false;
            }
            return this.getWebchatInstance()
                .isChoiceSelectionMultipleAutoComplete(message, this.getInstance().multipleChoiceLimitCount);
        },
        isMessageContinueConversation(msg = {}) {
            const { payload } = msg;
            if (!payload) {
                return false;
            }
            const { selectedChoices } = payload;
            if (!selectedChoices) {
                return false;
            }
            const [command] = selectedChoices;
            // code is frontend only reference, and only used by frontend web
            // declation source is from mocks.ts/timeoutMessagePayload
            return command && command === "command.continue_conversation";
        },
        isAdamActionCodeRestart(adamActions = []) {
            if (!adamActions) {
                return false;
            }
            const [adamItem] = adamActions;
            return adamItem && adamItem.code === "conversation.session.restart";
        },
        isMessageRestartNode(msg = {}) {
            const { payload } = msg;
            if (!payload) {
                return false;
            }
            const { text } = payload;
            const restartText = strings.defaultTimeoutRestartMessage.toLocaleLowerCase();
            if (text && text.toLocaleLowerCase() === restartText) {
                // if node is restart inside timeout message
                const { content } = payload;
                if (!content) {
                    return false;
                }
                const [contentItem] = content;
                if (!contentItem) {
                    return false;
                }
                const { adamActions } = contentItem;
                return this.isAdamActionCodeRestart(adamActions);
            }
            else {
                // if node is restart from standalone button
                const { adamActions } = payload;
                return this.isAdamActionCodeRestart(adamActions);
            }
        },
        isMessageTimeoutNode(msg = {}) {
            const { payload } = msg;
            if (!payload) {
                return false;
            }
            const { adamActions } = payload;
            if (!adamActions) {
                return false;
            }
            const [adamItem] = adamActions;
            return adamItem && adamItem.code === "conversation.status.timeout";
        },
        isMessageFinalNode(msg = {}) {
            const { payload } = msg;
            if (!payload) {
                return false;
            }
            const { adamActions } = payload;
            if (!adamActions) {
                return false;
            }
            const [adamItem] = adamActions;
            return adamItem && adamItem.code === "conversation.status.end";
        },
        /**
         * Confirms the message is of type chatText
         * @param {object} msg - the message object payload
         * @returns {boolean}
         */
        isMessageChatText(msg = {}) {
            const { type, action, text } = this.getMessageChatTextPayload(msg);
            return type === "chat_text" && action === "chat_text" && !!text;
        },
        isMessageResponseIncoming(message) {
            const { stanza } = message;
            if (stanza) {
                const { direction } = stanza;
                return direction === "in";
            }
            else {
                return false;
            }
        },
        isLastConversationOutgoingMessage(currentConversation = []) {
            const lastItem = currentConversation.slice(-1);
            const [lastItemObj] = lastItem;
            if (!lastItemObj) {
                return false;
            }
            return !isIncomingMessage(lastItemObj);
        },
        isMessageAdamService(message) {
            const { type, action } = message;
            return type === "adam_service" && action === "adam_service";
        },
        isMessagePrivacyResponse(message) {
            let isPrivacy = false;
            if (message) {
                const { selectedChoices } = message.payload;
                if (selectedChoices && selectedChoices.length) {
                    const [choice] = selectedChoices;
                    const privacyChoices = ["privacy_ok", "privacy_ko"];
                    isPrivacy = privacyChoices.includes(choice);
                }
            }
            return isPrivacy;
        },
        isMessageHasStanza(msg) {
            if (!msg) {
                return false;
            }
            const { stanza } = msg;
            return !!stanza;
        },
        isMessageHasStanzaWithEqualThread(msg) {
            if (!msg) {
                return false;
            }
            const { stanza } = msg;
            return (this.isMessageHasStanza(msg) &&
                stanza.hasOwnProperty("thread") &&
                stanza.thread === this.getCurrentConversationThread());
        },
        isLastConversationPrivacyResponse(currentConversation = []) {
            const lastItem = currentConversation.slice(-1);
            const [lastItemObj] = lastItem;
            if (!lastItemObj) {
                return false;
            }
            return this.isMessagePrivacyResponse(lastItemObj);
        },
        isMessageOkToBeAddedToConversation(msg, currentMessage) {
            const isPrivacyMessage = this.isMessagePrivacyResponse(msg);
            const isMessageAdamService = this.isMessageAdamService(msg);
            const isMessageRestartNode = this.isMessageRestartNode(msg);
            const currentMamId = this.getWebchatInstance().getMamIdFromStanza(msg.stanza);
            const isSpocMessageAlreadyExists = this.getWebchatInstance().isMamMessageExistsOnConversation([...currentMessage], msg, currentMamId);
            return (!isPrivacyMessage &&
                !isMessageAdamService &&
                (
                // from mam query, not a restart node
                (!isSpocMessageAlreadyExists && !isMessageRestartNode) ||
                    // a restart node, but from mam query
                    (currentMamId && !isSpocMessageAlreadyExists && isMessageRestartNode) ||
                    // a restart node, from realtime chat reset
                    (!currentMamId && isMessageRestartNode)));
        },
        /**
         * Toggle attachment icon
         * @param {boolean} canShow - A flag that tells if the attachments are enabled or not
         * @returns {void}
         */
        prepareAttachmentsInformations(canShow = true) {
            let config = this.getWebchatInstance().attachmentsUploaderConfig;
            if (canShow) {
                const { connection } = this.getWebchatInstance().xmppInstance;
                const JID = connection ? connection.authcid : false;
                if (JID) {
                    config = {
                        ...config,
                        requestData: {
                            user: `${JID}:${this.getCurrentConversationThread()}`
                        }
                    };
                }
            }
            this.getWebchatInstance().setAttachmentsUploaderConfig({
                ...config,
                use: canShow
            });
        },
        // this is a function wrapper
        // triggered inside core
        handleOnRestartChat() {
            this.getInstance().handleRestartMessage();
        },
        handleAdamActionMessageCodes(code) {
            switch (code) {
                case adamActionsConfig[0]: // conversation.chat.disable
                case adamActionsConfig[1]: // conversation.chat.enable
                    const isShowInput = code === adamActionsConfig[1];
                    // toggle chat input
                    if (this._webchatReference) {
                        this._webchatReference.toggleUserInputArea(isShowInput);
                        this._webchatReference.toggleUserInputStatus(isShowInput);
                    }
                    break;
                case adamActionsConfig[4]: // conversation.typing.stop
                case adamActionsConfig[5]: // conversation.typing.start
                    const isStartTyping = code === adamActionsConfig[5];
                    this.getInstance().handleShowIsTypingBubble(isStartTyping);
                    break;
                default:
                    break;
            }
        },
        handleIncomingMessageAdamActions(message) {
            const { payload } = message;
            if (payload) {
                const { adamActions } = payload;
                if (adamActions) {
                    adamActions.forEach(({ code }) => {
                        this.handleAdamActionMessageCodes(code);
                    });
                }
            }
        },
        handleIncomingMessageInputInteractions(msg) {
            // handle incoming location node
            if (this.isMessageInputLocation(msg)) {
                this.getWebchatInstance().setIsInteractionInsertLocation(true);
            }
            else {
                this.getWebchatInstance().setIsInteractionInsertLocation(false);
            }
            // handle incoming input date node
            if (this.isMessageInputDate(msg)) {
                const { payload } = msg;
                const { inputData } = payload;
                this.getWebchatInstance().setIsInteractionInsertDate(true);
                this.getWebchatInstance().setCurrentDatePickerInfo({
                    status: "input_date",
                    type: inputData.interaction.options.dateType || "",
                    options: inputData.interaction.options,
                });
            }
            else {
                this.getWebchatInstance().setIsInteractionInsertDate(false);
                this.getWebchatInstance().setCurrentDatePickerInfo({});
            }
            const isSingleAutocomplete = this.isMessageSingleAutocomplete(msg);
            const isMultipleAutocomplete = this.isMessageMultipleAutocomplete(msg);
            // handle incoming autocomplete for single/multiple choices selection
            if (isSingleAutocomplete || isMultipleAutocomplete) {
                const { payload } = msg;
                const { inputData } = payload;
                const { choice } = inputData;
                const { list: choiceList } = choice;
                if (isSingleAutocomplete) {
                    // handle incoming autocomplete for single choices selection
                    console.log("message node is single autocomplete...");
                    this.getWebchatInstance().setIsInteractionSingleAutocomplete(true);
                    this.getWebchatInstance().setIsInteractionMultipleAutocomplete(false);
                }
                else if (isMultipleAutocomplete) {
                    // handle incoming autocomplete for multiple choices selection
                    console.log("message node is multiple autocomplete...");
                    this.getWebchatInstance().setIsInteractionMultipleAutocomplete(true);
                    this.getWebchatInstance().setIsInteractionSingleAutocomplete(false);
                }
                else {
                    this.getWebchatInstance().setIsInteractionSingleAutocomplete(false);
                    this.getWebchatInstance().setIsInteractionMultipleAutocomplete(false);
                }
                this.getWebchatInstance().setCurrentAutocompleteInfo({ ...msg });
                this.getWebchatInstance().setCurrentAutocompleteList([...choiceList]);
            }
            else {
                this.getWebchatInstance().setIsInteractionSingleAutocomplete(false);
                this.getWebchatInstance().setIsInteractionMultipleAutocomplete(false);
                this.getWebchatInstance().setCurrentAutocompleteInfo({});
                this.getWebchatInstance().setCurrentAutocompleteList([]);
            }
        },
        handleFireUnreadMessages(keyThread) {
            // just put this into a wrapper function
            // so from here we can add logics later on
            this.getWebchatInstance().handleUnreadMessages(keyThread);
        },
        handleFireResetUnreadMessages(keyThread, mode = "all", msg) {
            if (mode === "selective") {
                const mainThread = this.getInstance().getMainThread();
                const { currentChat } = this.getWebchatInstance();
                const isChatView = Object.entries(currentChat).length > 0;
                // handle for support conversations closed by the agent
                if (msg && msg.hasOwnProperty("payload")) {
                    if (msg.payload.hasOwnProperty("code")) {
                        // noticeCodes[1] is GENESYS#0002
                        // "This conversation has now been closed by the agent"
                        if (msg.payload.code === noticeCodes[1]) {
                            // reset unread counter
                            this.getWebchatInstance().resetUnreadMessagesCount(keyThread);
                            // TODO: confirm disable input area
                        }
                    }
                }
                if (isChatView) {
                    const { class: currentClass, thread: currentThread } = currentChat;
                    // if current thread is spoc
                    if (currentClass === ConversationType.Wfp) {
                        // console.log("reset spoc conversation...");
                        // reset unread messagges count
                        this.getWebchatInstance().resetUnreadMessagesCount(mainThread);
                    }
                    else {
                        // if current thread is a support conversation
                        if (currentThread === keyThread) {
                            // console.log("reset human conversation...");
                            this.getWebchatInstance().resetUnreadMessagesCount(currentThread);
                        }
                    }
                }
            }
            else {
                // reset unread messagges count
                this.getWebchatInstance().resetUnreadMessagesCount(keyThread);
            }
        },
        handleFireShowTypingBubble(msg) {
            const { class: currentChatClass } = this.getWebchatInstance().currentChat;
            const isWfp = currentChatClass === ConversationType.Wfp;
            const { getCurrentChatMessages } = this.getWebchatInstance();
            if (isWfp &&
                this.isLastConversationOutgoingMessage(getCurrentChatMessages) &&
                !this.isMessageRestartNode(msg)) {
                console.log("show is typing bubble...");
                // handle debounce show typing bubble
                // here, just add a debounce so it will trigger last
                // this.getInstance().debounceShowTypingBubble(true);
                this.getInstance().handleShowIsTypingBubble(true);
            }
        },
        getLatestIncomingMessages(currentConversation = []) {
            const lastItem = currentConversation.slice(-1);
            const [lastItemObj] = lastItem;
            const latestMessages = [];
            if (lastItemObj && this.isMessageResponseIncoming(lastItemObj)) {
                const { payload } = lastItemObj;
                if (payload) {
                    const { content } = payload;
                    if (content) {
                        content.forEach(({ html, text }) => {
                            if (html) {
                                latestMessages.push(getParseTextContentFromHtml(html));
                            }
                            else {
                                latestMessages.push(text);
                            }
                        });
                    }
                }
            }
            return latestMessages;
        },
        getUpdatedCurrentConversationMessage(msg, conversationMessages = [], keyThread) {
            // create a shallow copy
            const currentConversationMessages = [...conversationMessages];
            const { isXmppQueryArchivePagination, shouldForceQueryArchivePagination, isCurrentConversationPaginationModeForward, } = this.getWebchatInstance();
            if (this.isMessageResponseIncoming(msg) && !isXmppQueryArchivePagination) {
                // handle disable is typing bubble
                this.getInstance().handleShowIsTypingBubble(false);
                // handle adamActions for incoming message
                this.handleIncomingMessageAdamActions(msg);
                // handle incoming input interactions
                // i.e. detecting input location nodes, input date nodes, autocomplete nodes
                this.handleIncomingMessageInputInteractions(msg);
            }
            const { getCurrentChatMessages } = this.getWebchatInstance();
            const isPrivacyMessage = this.isMessagePrivacyResponse(msg);
            const isLastMessagePrivacyResponse = this.isLastConversationPrivacyResponse(getCurrentChatMessages);
            const isMessageOkToBeAddedToConversation = this.isMessageOkToBeAddedToConversation(msg, [...currentConversationMessages]);
            if (isMessageOkToBeAddedToConversation) {
                console.log("message is ok to be added to conversation...");
                // for bot response(incoming)
                // and user response(outgoing)
                // but not user privacy response
                // and not adam_service response
                if (isXmppQueryArchivePagination || shouldForceQueryArchivePagination) {
                    // and if messages is from pagination
                    console.log("receiving mam with pagination message...");
                    // we have to wait all the previous messages to arrive first
                    // so lets stage the results of the pagination for the moment
                    // and only prepend to the currentConversationMessage when all messages arrives
                    // then the handleDisplayPreviousMessages() from the webchat-core-wfp module
                    // will handle the sorting and the realignment to local storage
                    this.getWebchatInstance().pushMessageInCurrentConversationPagination(msg);
                    // fire unread message
                    if (isCurrentConversationPaginationModeForward) {
                        this.handleFireUnreadMessages(keyThread);
                    }
                }
                else {
                    // default mam query
                    // and not messages from pagination
                    console.log("receiving mam message...");
                    // add message to current conversation
                    this.getWebchatInstance().pushMessageInCurrentConversation(msg);
                    // add to conversation message tree 
                    currentConversationMessages.push(msg);
                    // Add message into messages array store
                    this.getWebchatInstance().addMessage(msg);
                    // Add message into conversation history
                    this.getWebchatInstance().addMessageToHistory(msg);
                    // handle input modes before/after
                    this.getInstance().handleInputModesBeforeAfter(msg);
                    // fire unread messages
                    this.handleFireUnreadMessages(keyThread);
                }
            }
            else if (isPrivacyMessage && !isLastMessagePrivacyResponse) {
                // if user privacy response(accept/reject)
                // we put a hardcheck to only store the response msg once
                // TODO: check if this is needed in the future, otherwise remove
                // no user privacy response for wfp atm 
                // this.getWebchatInstance().pushMessageInCurrentConversation(msg);
            }
            return currentConversationMessages;
        },
        /**
         * Stores message on UI, in Vuex store and into localStorage (?)
         * @override MonkChatCore.storeMessage method
         * @param {any} msg - The message to store
         * @param {boolean} storeInLS - A flag that tells if msg has to be stored in localStorage or not
         * @returns {void}
         */
        storeMessage(msg, storeInLS = true) {
            // Retrieve messages
            const localMessagesTree = this.getInstance().getLocalMessagesDictionary();
            // Check for current conversation thread
            const keyThread = this.isMessageHasStanza(msg) && msg.stanza.thread !== "anonymous" ?
                msg.stanza.thread : this.getCurrentConversationThread();
            // console.log("mainThread: ", this.getInstance().getMainThread());
            // console.log("keyThread: ", keyThread);
            // Check if current messages array is already created or not
            if (!localMessagesTree.hasOwnProperty(keyThread)) {
                localMessagesTree[keyThread] = [];
            }
            // get constructed message
            msg = Object.assign({}, this.getConstructedStoreMessage(msg));
            const { isXmppQueryArchivePagination, isCurrentConversationPaginationModeForward, } = this.getWebchatInstance();
            const canAddMessageToCurrentConversation = keyThread === this.getCurrentConversationThread();
            // console.log("canAddMessageToCurrentConversation: ", canAddMessageToCurrentConversation);
            // console.log("msg: ", JSON.stringify(msg));
            // Check if message can be added to currently open conversation
            if (canAddMessageToCurrentConversation) {
                // handle add message to current conversation
                localMessagesTree[keyThread] = this.getUpdatedCurrentConversationMessage(msg, localMessagesTree[keyThread], keyThread);
            }
            else {
                // check if message is from support mam archives
                const mainThread = this.getInstance().getMainThread();
                const { key: msgThread } = msg;
                const threadKeysArr = Object.keys(localMessagesTree)
                    .filter((thread) => {
                    return thread.includes(`${mainThread}|$|`);
                });
                // console.log("threadKeysArr: ", threadKeysArr);
                // only add to store if conversation is from support
                if (threadKeysArr.includes(keyThread)) {
                    // if msg thread is the same as key thread
                    // means archive case already exists
                    if (msgThread === keyThread) {
                        const currentMamId = this.getWebchatInstance().getMamIdFromStanza(msg.stanza);
                        // check for duplicates
                        const isMessageAlreadyExists = this.getWebchatInstance().isMamMessageExistsOnConversation([...localMessagesTree[keyThread]], msg, currentMamId);
                        // console.log("isMessageAlreadyExists: ",isMessageAlreadyExists);
                        // console.log("currentMamId: ", currentMamId);
                        if (!isMessageAlreadyExists || !currentMamId) {
                            if (isCurrentConversationPaginationModeForward) {
                                console.log("receiving mam message with forward pagination from support...");
                                localMessagesTree[keyThread].push(msg);
                                // Add message into messages array store
                                this.getWebchatInstance().addMessage(msg);
                                // Add message into conversation history
                                this.getWebchatInstance().addMessageToHistory(msg);
                                // fire unread message
                                this.handleFireUnreadMessages(keyThread);
                            }
                            else {
                                console.log("receiving mam message with backward pagination from support...");
                                // again, we have to wait all the previous support messages to arrive first
                                // so lets stage the results of the pagination for the moment
                                // and only prepend to the correct thread when all messages arrives
                                this.getWebchatInstance().pushMessageInCurrentConversationSupportPagination({
                                    thread: keyThread,
                                    message: msg,
                                });
                            }
                        }
                    }
                }
                else {
                    // this happens when user switch to a support conversation
                    // while spoc hasnt replied yet, 
                    // then apparently, spoc replies late
                    // so we only detect spoc message
                    if (keyThread === mainThread) {
                        console.log("message is from spoc while a support conversation is open...");
                        const isMessageOkToBeAddedToConversation = this.isMessageOkToBeAddedToConversation(msg, [...localMessagesTree[keyThread]]);
                        // always check if message is ok be added
                        if (isMessageOkToBeAddedToConversation) {
                            console.log("message is ok to be added to spoc conversation...");
                            // Add message into conversation history
                            this.getWebchatInstance().addMessageToHistory(msg);
                            // handle add message to the conversation
                            localMessagesTree[keyThread].push(msg);
                            // handle disable is typing bubble
                            this.getInstance().handleShowIsTypingBubble(false);
                            // fire unread messages
                            this.handleFireUnreadMessages(keyThread);
                        }
                    }
                }
            }
            // Update localStorage
            this._slsSet(this.getInstance()._getLocalMessageTree, localMessagesTree);
            // Track last message timestamp for mam query
            const { stanza } = msg;
            let lastMsgTimestamp = stanza ? stanza.timestamp : msg.timestamp;
            if (storeInLS) {
                // store timestamp in local storage
                localStorage.setItem(this.getInstance()._getLocalLastMessageTimestamp, lastMsgTimestamp);
                // handle debounce timeout message
                // the idea is very simple, less complicated and less logic
                // here, just add a debounce timeout with a
                // configurable inactive time (i.e. 20 mins)
                this.getInstance().debounceTimeoutMessage(msg, lastMsgTimestamp);
            }
            if (!isXmppQueryArchivePagination) {
                // handle debounce auto scrolldown conversation
                // here, just add a debounce so it will trigger last
                this.getInstance().debounceScrollDownConversation(keyThread, "selective", msg);
            }
            // handle debounce voice interface
            // here, just add a debounce for firing the voice interface
            // so it will trigger last
            this.getInstance().debounceHandleVoiceInterface(msg);
        },
        /*
         * returns modified payload for privacy message
         * i.e. { payload, adamActions }
         * removes adamActions: [] inside content
         * and reposition to the same hierarchy with content
         * otherwise return empty { adamActions: [] }
         * @param {any} payload - The message to send via XMPP
         */
        getWelcomePrivacyMessagePayload(payload = {}) {
            const { text, content } = payload;
            const privacyResponses = ["Accetto", "Rifiuto"];
            const emptyAdamActions = { adamActions: [] };
            if (privacyResponses.includes(text) && content) {
                const [privacyContent] = content;
                const { adamActions } = privacyContent;
                // sometimes adamActions is undefined
                if (adamActions) {
                    const [adamActionsContent] = adamActions;
                    delete privacyContent.adamActions;
                    return {
                        payload: {
                            ...payload,
                            content: [{ ...privacyContent }]
                        },
                        adamActions: [
                            {
                                ...adamActionsContent,
                                args: [adamActionsContent.args]
                            }
                        ]
                    };
                }
                else {
                    return { ...emptyAdamActions };
                }
            }
            else {
                return { ...emptyAdamActions };
            }
        },
        /*
         * returns modified payload for timeout message
         * i.e. {
         *  payload: {
         *    text: "Start a new conversation|Continue the previous conversation",
         *    adamActions: [{
         *      // conversation.session.continue_conversation is frontend only
         *      code: "conversation.session.restart|conversation.session.continue_conversation"
         *    }]
         *  },
         *  isTimeoutPayload: true
         * }
         * otherwise, do nothing return { isTimeoutPayload: false }
         * @param {any} payload - The message to send via XMPP
         */
        getTimeoutMessagePayload(payload = {}) {
            const { text, content } = payload;
            const timeoutResponses = [strings.defaultTimeoutContinuePreviousMessage, strings.defaultTimeoutRestartMessage];
            if (!timeoutResponses.includes(text) || !content) {
                return { isTimeoutPayload: false };
            }
            const [timeoutContent] = content;
            const { adamActions } = timeoutContent;
            if (!adamActions) {
                return { isTimeoutPayload: false };
            }
            const [adamActionsContent] = adamActions;
            const { code } = adamActionsContent;
            return {
                payload: {
                    text,
                    adamActions: [{ code }]
                },
                isTimeoutPayload: true
            };
        },
        /**
         * Send msg override
         * @override MonkChatCore.sendMessageAction method
         * @param {any} msg - The message to send
         * @returns {void}
         */
        sendMessageAction(msg) {
            if (this.isMessageContinueConversation(msg)) {
                // this is only for timeout message
                // with a response of "continue conversation"
                // we need the magic function below, because the goal is:
                // 1. when, continue conversation is click
                // 2. remove the timeout message from the current conversation
                // 3. show the last message before the timeout message
                // 4. provided that, all its interactivity must remain the same
                console.log("continue conversation from timeout...");
                this.getInstance().handleAdjustedConversationWithoutTimeoutMessage("continue");
                return false;
            }
            if (this.isMessageRestartNode(msg)) {
                console.log("restarting conversation from timeout...");
                this.getInstance().handleAdjustedConversationWithoutTimeoutMessage("restart");
            }
            // validate input for msg type chatText
            if (this.isMessageChatText(msg)) {
                const { text } = this.getMessageChatTextPayload(msg);
                /**
                 * return false if message from input
                 * is not validated and contains exploits
                 * normally some xss attacks
                 */
                if (!this.isMessageInputValidated(text)) {
                    return false;
                }
            }
            if (!this.isMessageHasStanza(msg)) {
                Object.assign(msg, { timestamp: moment().format() });
            }
            this.sendXMPPMessage(msg);
            this.storeMessage(msg);
            // show is typing bubble
            setTimeout(() => {
                this.handleFireShowTypingBubble(msg);
            }, 300);
        },
        /**
         * Send msg over XMPP
         * @override MonkChatCore.sendXMPPMessage method
         * @param {any} msg - The message to send via XMPP
         * @returns {void}
         */
        sendXMPPMessage(msg) {
            const timestamp = moment().format();
            const { currentChatThread, _parsedURIParamsForMessagePayloadData } = this.getInstance();
            const thread = currentChatThread || this.getCurrentConversationThread();
            // Creates XMPP message structure
            const message = {
                to: this.getInstance()._getReceiver,
                body: " ",
                timestamp,
                thread
            };
            // here intercept payload to check for
            // accept/reject welcome privacy message
            const { adamActions, payload } = this.getWelcomePrivacyMessagePayload({ ...msg.payload });
            // only modify payload if adamActions is available
            if (adamActions && adamActions.length && payload) {
                const messageResponse = { ...msg };
                msg = {
                    ...messageResponse,
                    payload: {
                        ...payload,
                        adamActions
                    }
                };
                // enable privacyMessageClick
                this.getInstance().toggleIsPrivacyMessageClick(true);
                // disable isUserRelogin flag
                this.getInstance().isUserRelogin = false;
            }
            // intercept checkpoint for timeout message payload(i.e. continue/restart)
            const { isTimeoutPayload, payload: payloadTimeout } = this.getTimeoutMessagePayload({ ...msg.payload });
            // only modify payload if isTimeoutPayload is true
            if (isTimeoutPayload && payloadTimeout) {
                const messageResponse = { ...msg };
                msg = {
                    ...messageResponse,
                    payload: {
                        ...payloadTimeout
                    }
                };
            }
            // Add additional data getting it from URL
            Object.assign(msg.payload, {
                data: _parsedURIParamsForMessagePayloadData
            });
            // Send XMPP message via Huangcha
            try {
                console.log("sendXMPPMessage payload msg: ", JSON.stringify(msg));
                this.getWebchatInstance().xmppInstance.sendMonkMessage(message, msg, { askReceipt: true });
                // Handle adamActions
                const { content } = msg.payload;
                if (content) {
                    content.forEach((item) => this.handleAdamActions(item));
                }
            }
            catch (err) {
                console.log("sendXMPPMessage error: ", err);
            }
        },
        /**
         * Fetch messages from localStorage or from the server and trigger all the observers
         * @override MonkChatCore.retrieveOlderMessages method
         * @returns {void}
         */
        retrieveOlderMessages() {
            const localMessagesTree = this.getInstance().getLocalMessagesDictionary();
            const { class: currentChatClass } = this.getWebchatInstance().currentChat;
            const isWfp = currentChatClass === ConversationType.Wfp;
            // get mam from 3 days ago
            // last updated 1/30/25
            // const correctedTimestamp = this.getInstance().getCorrectedTimestamp(3);
            const correctedTimestamp = this.getInstance().getCorrectedTimestamp(3);
            const receiver = this.getInstance()._getReceiver;
            const localMessagesTreeValues = Object.values(localMessagesTree);
            if (localMessagesTree && localMessagesTreeValues.length > 0) {
                localMessagesTreeValues.forEach((conversation) => {
                    conversation.forEach((msg) => {
                        const { thread } = msg;
                        // Check if message has thread field (inside payload > data)
                        if (thread) {
                            msg.thread = thread;
                        }
                        // TODO: understand why we even need this? 
                        this.getWebchatInstance().addMessage(msg);
                    });
                });
                // Check for eventual messages from server, starting from most recent message.
                // This is useful to fetch messages between now and the last time that the user used the chat.
                // TODO: revisit if this comes first or the handleWelcomeMessage below
                if (isWfp) {
                    localStorage.setItem(this.getInstance()._getLocalLastMessageTimestamp, correctedTimestamp);
                    console.log("has localMessagesTree corrected timestamp for spoc: ", correctedTimestamp);
                }
                const mainThread = this.getInstance().getMainThread();
                if (localMessagesTree.hasOwnProperty(mainThread)) {
                    // we need to first, toggle force query archive pagination
                    this.getWebchatInstance().setShouldForceQueryArchivePagination(true);
                    // then, get lastMamId
                    const lastMamId = this.getWebchatInstance()
                        .getXmppMamPaginationLastId(localMessagesTree[mainThread], true);
                    console.log("lastMamId before fetching next messages: ", lastMamId);
                    // call mam after pagination request
                    this.getWebchatInstance().handleFetchNextMessages(lastMamId);
                }
            }
            else {
                // ...Otherwise fetches them from the server
                // only correct the timestamp for spoc conversations
                if (isWfp) {
                    // set corrected timestamp to localStorage
                    localStorage.setItem(this.getInstance()._getLocalLastMessageTimestamp, correctedTimestamp);
                    console.log("no localMessagesTree corrected timestamp for spoc: ", correctedTimestamp);
                }
                this.getWebchatInstance().sendRawMamIqRequest({
                    receiver,
                    beforeMsgId: "",
                    afterMsgId: false,
                    max: this.lastMessageToDisplayTotal,
                    isQueryArchive: true,
                });
                // this.getInstance().handleWelcomeMessage(true);
            }
            // TODO: remove after dev
            /* setTimeout(() => {
              // console.log('render mock restart....');
              // this.getInstance().handleRestartMessage();
      
              console.log('render mock timeout message....');
              this.getInstance().handleTimeoutMessage(true);
            }, 1000); */
            // Check for special URL params to perform quick actions (usually send messages without XMPP interaction)
            this.getInstance().checkChatQuickActionsFromURI();
        },
        /**
         * Handle UX based on incoming message
         * @override MonkChatCore.fillOutMessagesList method
         * @param {any} msg - The message that triggered this observer
         * @returns {void}
         */
        async fillOutMessagesList(msg) {
            const localMessagesTree = this.getInstance().getLocalMessagesDictionary();
            let messageSender = "bot";
            let incomingThread = "";
            if (msg) {
                const { payload, stanza } = msg;
                // handle sender
                messageSender =
                    payload && payload.hasOwnProperty("data") && payload.data.hasOwnProperty("conversationType")
                        ? msg.payload.data.conversationType
                        : "bot";
                // handle thread
                if (stanza) {
                    const { thread } = stanza;
                    incomingThread = thread;
                }
            }
            const currentConversationPaginationMode = this.getWebchatInstance().getCurrentConversationPaginationMode;
            if (isMamMessage(msg) && currentConversationPaginationMode === "forward") {
                const lastMamId = this.parseMamArchiveIdFromXML(msg.stanza.rawStanza);
                // console.log("fillOutMessagesList lastMamId: ", lastMamId);
                localStorage.setItem(this.getInstance()._getLocalLastMamMessageId, lastMamId);
            }
            // Perform logic on messages only if there's a valid thread
            if (incomingThread) {
                if (isMamMessage(msg)) {
                    Object.assign(msg, {
                        timestamp: this.getInstance().extractTimestampFromMamMessage(msg)
                    });
                }
                if (!localMessagesTree.hasOwnProperty(incomingThread)) {
                    localMessagesTree[incomingThread] = [];
                }
                // Redundant logic...
                // Reset thread if it's guest message
                incomingThread = incomingThread === "anonymous" ? "" : incomingThread;
                const splitConversations = this.getWebchatInstance().checkIfChatIsMultiLevel(incomingThread);
                // Check if it's message from human...
                if (splitConversations.length > 0) {
                    // handle conversation message from human
                    await this.handleConversationMessageFromHuman(incomingThread, messageSender, localMessagesTree, msg);
                }
                else {
                    // if message from SPOC
                    // disable isTyping bubble
                    // this.getInstance().handleShowIsTypingBubble(false);
                    console.log("fillout messages spoc...");
                    this.storeMessage(msg);
                    // handle debounce archive conversation
                    // the idea is very simple, less complicated and less logic
                    // here, just add a debounce so it will trigger last
                    // but only if mam query is not pagination
                    const { isXmppQueryArchivePagination } = this.getWebchatInstance();
                    if (!isXmppQueryArchivePagination) {
                        this.getInstance().debounceArchiveConversationMessage();
                    }
                }
            }
        },
        async handleConversationMessageFromHuman(incomingThread, messageSender, localMessagesTree, msg) {
            const localConversationsTree = this.getInstance().getLocalConversationsArray();
            const splitConversations = this.getWebchatInstance().checkIfChatIsMultiLevel(incomingThread);
            let canUpdateConversationsTree = true;
            // Extract conversation title
            const conversationTitle = msg.hasOwnProperty("payload") &&
                msg.payload.hasOwnProperty("data") &&
                msg.payload.data.hasOwnProperty("chatTitle")
                ? msg.payload.data.chatTitle
                : atob(splitConversations[1].split("_")[0]);
            // Create new conversation if hasn't been already created...
            const isLocalConversationExisted = localConversationsTree.find((item) => item.thread === incomingThread);
            // console.log("isLocalConversationExisted: ", isLocalConversationExisted);
            if (!isLocalConversationExisted) {
                // ... if not, create it
                const newConversationToAdd = {
                    id: `${incomingThread}__${Date.now()}`,
                    title: conversationTitle,
                    class: ConversationType.Active,
                    avatar: this.getInstance()._getRenderedAsset.ico_operatore,
                    thread: incomingThread,
                    is_archived: false,
                    timestamp: isMamMessage(msg)
                        ? !this.isMessageHasStanza(msg)
                            ? msg.timestamp
                            : msg.stanza.timestamp
                        : moment().format(),
                };
                localConversationsTree.push(newConversationToAdd);
                await this.getWebchatInstance().pushConversation(newConversationToAdd);
                // Redundant logic...
                // Handle FOREGROUND chat notice (if it's a notice)
                if (!isMamMessage(msg)) {
                    this.getWebchatInstance().handleNotice(msg);
                }
            }
            else {
                // ... Otherwise, if conversation is already created
                // Check if message is a chat_notice
                if (msg.payload.hasOwnProperty("code")) {
                    const currentChatIndex = localConversationsTree.findIndex((lc) => lc.thread === incomingThread);
                    // Handle notices
                    switch (msg.payload.code) {
                        case noticeCodes[0]: // GENESYS#0001
                        case noticeCodes[1]: // GENESYS#0002
                        case noticeCodes[3]: // GENESYS#0004
                            if (currentChatIndex !== -1) {
                                // Genesys close message, but not when chat's archived
                                if (msg.payload.code === noticeCodes[3]) {
                                    if (localConversationsTree[currentChatIndex].class !== ConversationType.Archived) {
                                        localConversationsTree[currentChatIndex].class = ConversationType.Deleted;
                                    }
                                    else if (localConversationsTree[currentChatIndex].class === ConversationType.Archived) {
                                        localConversationsTree[currentChatIndex].is_archived = true;
                                    }
                                }
                                else if (localConversationsTree[currentChatIndex].class !== ConversationType.LocalClosed) {
                                    localConversationsTree[currentChatIndex].class = ConversationType.RemoteClosed;
                                }
                            }
                            // handle UI checking context
                            if (this.isMessageHasStanzaWithEqualThread(msg)) {
                                this.getInstance().showCloseChatCta = false;
                                this.getWebchatInstance().toggleUserInputArea(false);
                            }
                            break;
                        case noticeCodes[4]: // GENESYS_CLOSE_BY_USER#0001
                            if (currentChatIndex !== -1) {
                                localConversationsTree[currentChatIndex].class = ConversationType.LocalClosed;
                                console.log("noticeCodes[4]: ", noticeCodes[4]);
                                this._slsSet(this.getInstance()._getLocalMessageTree, localMessagesTree);
                                // handle UI checking context
                                if (this.isMessageHasStanzaWithEqualThread(msg)) {
                                    this.getInstance().showCloseChatCta = false;
                                    this.getWebchatInstance().toggleUserInputArea(false);
                                }
                            }
                            break;
                        case noticeCodes[6]: // FOREGROUND_CONVERSATION#0001
                        case noticeCodes[7]: // FOREGROUND_CONVERSATION#0002
                            if (currentChatIndex !== -1) {
                                if (localConversationsTree[currentChatIndex]) {
                                    localConversationsTree[currentChatIndex].class = ConversationType.Active;
                                }
                            }
                            if (!isMamMessage(msg)) {
                                this.getWebchatInstance().handleNotice(msg);
                            }
                            break;
                    }
                    // Update conversation class
                    if (currentChatIndex !== -1) {
                        await this.getWebchatInstance().updateConversationClass({
                            thread: localConversationsTree[currentChatIndex].thread,
                            newClass: localConversationsTree[currentChatIndex].class
                        });
                    }
                }
                else if (msg.payload.hasOwnProperty("adamActions")) {
                    // Check if message is an adamAction
                    // Reset close CTA (hide it)
                    this.getInstance().showCloseChatCta = false;
                    // Handle close CTA
                    this.handleAdamActions(msg.payload);
                }
                else {
                    // Handle UI based on incoming message, like:
                    if (isIncomingMessage(msg)) {
                        const isCurrentChat = Object.entries(this.getWebchatInstance().currentChat).length >= 0;
                        const chatCanBeInteractedByUser = isCurrentChat &&
                            !this.getInstance().canShowConversationsView &&
                            incomingThread === this.getCurrentConversationThread() &&
                            messageSender !== "bot";
                        // Toggle close CTA
                        this.getInstance().showCloseChatCta = chatCanBeInteractedByUser;
                        // Toggle attachments
                        this.prepareAttachmentsInformations(chatCanBeInteractedByUser);
                        canUpdateConversationsTree = false;
                    }
                }
            }
            // Check if message can be added onto UI (avoid duplicates)
            if (this.isMessageHasToBeAdded(msg, incomingThread)) {
                if (isMamMessage(msg)) {
                    Object.assign(msg, {
                        timestamp: this.getInstance().extractTimestampFromMamMessage(msg)
                    });
                }
                this.storeMessage(msg);
            }
            if (canUpdateConversationsTree) {
                this._slsSet(this.getInstance()._getLocalConversationTree, localConversationsTree);
            }
        },
        /**
         * A WORKAROUND method, useful to update conversations UI array. Require refactor!
         * Update chat UI (title & icon) based on incoming message
         */
        sanitizeConversationsArray() {
            const conversationListRef = this.getWebchatInstance().$refs.conversationList;
            let sanitizedArray = [];
            if (!conversationListRef.isArchiveSection) {
                const localConversationsTree = this.getInstance().getLocalConversationsArray();
                sanitizedArray = localConversationsTree.reverse();
            }
            else {
                const archivedConversationsTree = this.getInstance().getLocalConversationsArray(true);
                sanitizedArray = archivedConversationsTree;
            }
            this.getWebchatInstance().setConversationsArray(sanitizedArray);
        },
        parseMamArchiveIdFromXML(xmlMessage) {
            const parser = new DOMParser();
            const xmlMsg = parser.parseFromString(xmlMessage, "text/xml");
            // @ts-ignore
            return xmlMsg.firstElementChild && xmlMsg.firstElementChild.firstElementChild
                ? xmlMsg.firstElementChild.firstElementChild.id
                : "";
        },
        /**
         * Checks if message can be addded into current conversation
         * @param {any} message - Incoming message
         * @param {string} keyThread - Conversation thread
         * @returns {void}
         */
        isMessageHasToBeAdded(message, keyThread) {
            const localMessagesTree = this.getInstance().getLocalMessagesDictionary() || {};
            const { stanza } = message;
            if (!stanza) {
                return false;
            }
            const { rawStanza } = stanza;
            if (!rawStanza) {
                return false;
            }
            if (!localMessagesTree.hasOwnProperty(keyThread)) {
                return true;
            }
            const currentMamId = this.getWebchatInstance().getMamIdFromStanza(message.stanza);
            let isNotMamMessage = false;
            // check if message exist
            const isMessageAlreadyExists = localMessagesTree[keyThread].find((item) => {
                const { stanza: stanzaItem } = item;
                // console.log("item: ", JSON.stringify(item));
                if (stanzaItem) {
                    const itemMamId = this.getWebchatInstance().getMamIdFromStanza(stanzaItem);
                    // console.log("itemMamId: ", itemMamId);
                    return itemMamId === currentMamId;
                }
                else {
                    // if dont have stanza, not a mam message
                    // console.log("itemMamId: none");
                    isNotMamMessage = true;
                    return false;
                }
            });
            // if message do not exist or
            // currentMamId do not exist(this happens for forwarded messages)
            return !isMessageAlreadyExists || !currentMamId;
        },
        handleRenderPrivacyMessageInputArea(currentConversation = []) {
            const { isUserRelogin, isPrivacyMessageClick, showIsTypingBubble } = this.getInstance();
            const hasMessages = currentConversation.length > 1;
            const showWelcomeMessage = 
            // 1st. case
            (isUserRelogin && hasMessages) ||
                // 3rd. case
                (!isUserRelogin && hasMessages && !showIsTypingBubble && !isPrivacyMessageClick) ||
                // 4th. case
                (!isUserRelogin && (!hasMessages || showIsTypingBubble));
            const forceIsWfp = 
            // 1st. case
            isUserRelogin ||
                // 2nd. case
                (!isUserRelogin && hasMessages && !showIsTypingBubble && isPrivacyMessageClick) ||
                // 3rd. case
                (!isUserRelogin && hasMessages && !showIsTypingBubble && !isPrivacyMessageClick);
            // 2nd. case
            const forceIsPrivacy = !isUserRelogin && hasMessages && !showIsTypingBubble && isPrivacyMessageClick;
            if (!!showWelcomeMessage) {
                console.log("handleRenderPrivacyMessageInputArea !!showWelcomeMessage handleWelcomeMessage...");
                this.getInstance().handleWelcomeMessage(true);
            }
        },
        /**
         * Shows selected conversation and toggles all the related UI components
         * @override MonkChatCore.renderCurrentConversation method
         * @param {any} conversation - Conversation to render
         * @returns {void}
         */
        renderCurrentConversation(conversation) {
            const { thread, class: conversationClass } = conversation;
            const keyThread = thread ? thread : this.getCurrentConversationThread();
            const canShowConversationsView = Object.entries(conversation).length === 0;
            const localMessagesTree = this._slsGet(this.getInstance()._getLocalMessageTree);
            this.getWebchatInstance().canShowConversationsView = canShowConversationsView;
            // is chat view
            if (!canShowConversationsView) {
                // disable force query pagination
                this.getWebchatInstance().setShouldForceQueryArchivePagination(false);
                if (conversationClass !== ConversationType.Wfp) {
                    this.getWebchatInstance().filterConversationsMessages(keyThread, "", "", localMessagesTree);
                    this.getInstance().currentChatThread = keyThread;
                    this.getInstance().setupConversationUI(conversation);
                    if (conversationClass === ConversationType.Active) {
                        console.log("support...");
                        this.prepareAttachmentsInformations();
                        this.getWebchatInstance().toggleUserInputArea(true);
                        this.getWebchatInstance().toggleUserInputStatus(true);
                        this.getInstance().handleWelcomeMessage();
                        this.getInstance().showCloseChatCta = !this.getInstance().canShowConversationsView;
                        // disable restart chat
                        this.getWebchatInstance().setShowRestartChatButton(false);
                        // reset
                        this.getWebchatInstance().setModeBeforeSubmit("");
                        this.getWebchatInstance().setVisibilityAfterSubmit("");
                        this.getInstance().handleShowIsTypingBubble(false);
                        // reset unread messagges
                        this.handleFireResetUnreadMessages(keyThread);
                    }
                    else {
                        console.log("...");
                        this.getWebchatInstance().toggleUserInputArea(false);
                        this.getInstance().showCloseChatCta = false;
                        // disable restart chat
                        this.getWebchatInstance().setShowRestartChatButton(false);
                        // reset unread messagges
                        this.handleFireResetUnreadMessages(keyThread, "selective");
                    }
                }
                else {
                    console.log("spoc...");
                    this.getWebchatInstance().filterConversationsMessages(keyThread, conversationClass, "", localMessagesTree);
                    this.getInstance().currentChatThread = keyThread;
                    this.getWebchatInstance().toggleUserInputStatus(true);
                    this.getInstance().showCloseChatCta = false;
                    this.prepareAttachmentsInformations(false);
                    this.getInstance().setupConversationUI(conversation);
                    // this.getInstance().handleWfpConversationReset();
                    this.getWebchatInstance().toggleUserInputArea(true);
                    // enable restart chat
                    this.getWebchatInstance().setShowRestartChatButton(true);
                    // TODO: confirm if input area is always enable
                    // theres been a report about input area being disable
                    // when comes from list view to conversation view
                    // handle input modes before/after
                    this.getInstance().handleInputModesBeforeAfter();
                    // reset unread messagges
                    this.handleFireResetUnreadMessages(keyThread);
                    // handle privacy message and input chat area
                    // this.handleRenderPrivacyMessageInputArea(getCurrentChatMessages);
                }
            }
            else {
                this.getInstance().showCloseChatCta = false;
            }
        },
        /**
         * Check for "adamActions" and perform logic on commands (if found)
         * @param {any} content - Message content
         * @returns {void}
         */
        handleAdamActions(content = {}) {
            const { adamActions } = content;
            if (!adamActions) {
                return false;
            }
            adamActions.forEach((action) => {
                let uiStatus = true;
                switch (action.code) {
                    case adamActionsConfig[0]:
                        // Disable user input area
                        uiStatus = false;
                        break;
                    default:
                        // Enable user input area
                        uiStatus = true;
                        break;
                }
                this.getWebchatInstance().toggleUserInputStatus(uiStatus);
            });
        }
    }
});
